<template>
  <div v-if="filters.length > 0" class="applied-container">
    <div class="applied-filters">
      <div
        v-for="filter in filters"
        :key="`${filter.type}-${filter.label}`"
        class="applied-filter"
        @click="filter.handler"
      >
        <span>{{ filter.type }}</span>
        <span>{{ filter.label }}</span>
        <font-awesome-icon icon="times" />
      </div>
      <a
        href="javascript:void(0)"
        class="clear"
        @click="clear"
      >Clear all</a>
    </div>
  </div>
</template>
<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import intersectionWith from 'lodash/intersectionWith';

export default {
  components: {
    FontAwesomeIcon,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    isSearching: {
      type: Boolean,
      default: false,
    },
    smartCollectionFilters: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    isWishlist: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    defaultStatus() {
      return this.isSearching || this.isWishlist ? 'all' : 'current';
    },
    filters() {
      const defaults = this.smartCollectionFilters;
      const filters = this.value;
      const labels = [];
      let label;

      if (
        !defaults.filters.includes('marketplace')
        && filters.marketplace === false
      ) {
        labels.push({
          type: 'AppSumo Select',
          label: 'Select only',
          handler: () => this.change('marketplace', null),
        });
      }

      if (
        !defaults.filters.includes('bf')
        && filters.bf === true
      ) {
        labels.push({
          type: 'Black Friday',
          label: 'Black Friday only',
          handler: () => this.change('bf', null),
        });
      }

      if (
        !defaults.filters.includes('sumoday')
        && filters.sumoday === true
      ) {
        labels.push({
          type: 'Sumo Day',
          label: 'Sumo Day only',
          handler: () => this.change('sumoday', null),
        });
      }

      if (
        !defaults.filters.includes('plan_type')
        && filters.plan_type !== 'all'
      ) {
        switch (filters.plan_type) {
          case 'lifetime-deal': label = 'Lifetime deal'; break;
          case 'limited-license': label = 'Annual'; break;
          case 'freebie': label = 'Freebie'; break;
          case 'subscription': label = 'Subscription'; break;
          case 'digital-download': label = 'Digital download'; break;
          default: label = null; break;
        }

        if (label) {
          labels.push({ type: 'Plan type', label, handler: () => this.change('plan_type', 'all') });
        }
      }

      const { defaultStatus } = this;
      if (
        (!defaults.filters.includes('status') || this.isWishlist)
        && filters.status !== defaultStatus
      ) {
        switch (filters.status) {
          case 'ending': label = 'Ending soon'; break;
          case 'expired': label = 'Unavailable'; break;
          case 'all': label = 'All'; break;
          case 'current': label = 'Current'; break;
          default: label = null; break;
        }

        if (label) {
          labels.push({ type: 'Status', label, handler: () => this.change('status', defaultStatus) });
        }
      }

      if (
        !defaults.filters.includes('average_rating')
        && (filters.average_rating > 0 && filters.average_rating < 5)
      ) {
        labels.push({ type: 'Avg rating', label: `${filters.average_rating} and up`, handler: () => this.change('average_rating', null) });
      }

      label = null;
      if (filters.price_range?.type === 'free') {
        label = 'Free';
      } else if (filters.price_range?.type === 'custom' && (filters.price_range?.price_from || filters.price_range?.price_to)) {
        if (filters.price_range?.price_from && filters.price_range?.price_to) {
          label = `$${filters.price_range.price_from} - $${filters.price_range?.price_to}`;
        } else if (filters.price_range.price_from) {
          label = `$${filters.price_range.price_from} -`;
        } else if (filters.price_range.price_to) {
          label = `$${filters.price_range.price_to}`;
        }
      }

      if (label) {
        labels.push({ type: 'Price', label, handler: () => this.change('price_range', { type: 'custom' }) });
      }

      if (Object.keys(filters.attributes).length > 0) {
        Object.keys(filters.attributes).forEach((key) => {
          if (
            ['group', 'category', 'subcategory'].includes(key)
            || defaults.attributes.includes(key)
          ) return;
          const value = filters.attributes[key];
          const attrib = this.$store.state.browse.attributes.find((a) => a.slug === key);

          if (!attrib) return;

          if (attrib.type === 'enumeration') {
            if (attrib.type_options.multiple === true) {
              const options = intersectionWith(
                attrib.type_options.options,
                value,
                (a, b) => a.value === b,
              );

              options.forEach((option) => {
                labels.push({
                  type: attrib.name,
                  label: option.name,
                  handler: () => this.removeEnumerationOption(key, option.value),
                });
              });
            } else {
              const option = attrib.type_options.options.find((o) => o.value === value);

              if (option) {
                labels.push({
                  type: attrib.name,
                  label: option.name,
                  handler: () => this.changeAttr(key, undefined),
                });
              }
            }
          } else if (attrib.type === 'boolean' && value === true) {
            labels.push({
              type: attrib.name,
              label: `${attrib.name} only`,
              handler: () => this.changeAttr(key, undefined),
            });
          }
        });
      }

      return labels;
    },
  },
  methods: {
    change(key, value) {
      // deep clone
      const filters = cloneDeep(this.value);
      set(filters, key, value);
      this.$emit('input', filters);
    },
    changeAttr(key, value) {
      const attributes = { ...this.value.attributes };
      if (typeof value !== 'undefined') {
        attributes[key] = value;
      } else {
        delete attributes[key];
      }
      this.change('attributes', attributes);
    },
    removeEnumerationOption(key, value) {
      const attributes = { ...this.value.attributes };

      if (typeof value !== 'undefined') {
        attributes[key] = attributes[key].filter((o) => o !== value);
        if (attributes[key].length === 0) {
          delete attributes[key];
        }
      }
      this.change('attributes', attributes);
    },
    clear() {
      // restore defaults
      const filters = {
        categories: [],
        average_rating: null,
        marketplace: null,
        bf: null,
        sumoday: null,
        plan_type: 'all',
        status: this.defaultStatus,
        price_range: {
          type: 'custom',
        },
        attributes: {},
      };

      if (this.value.attributes) {
        ['group', 'category', 'subcategory'].forEach((key) => {
          if (this.value.attributes[key]) {
            filters.attributes[key] = this.value.attributes[key];
          }
        });
      }

      this.$emit('input', {
        ...this.value,
        ...filters,
      });
      this.$emit('clear');
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~/styles/utils/breakpoints';

.applied-container {
  padding: 0;
  margin: 5px 0 0;
}

.applied-filters {
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
}

.applied-filter {
  background-color: #dadde5;
  border-radius: 4px;
  padding: 4px 16px;
  cursor: pointer;
  font-size: 16px;
  font-family: Roboto, sans-serif;
  margin-right: 10px;
  display: flex;
  flex-direction: column;
  position: relative;
  padding-right: 40px;
  white-space: nowrap;
  margin-bottom: 10px;
}

.applied-filter span:first-child {
  color: #434d59;
  font-size: 14px;
  line-height: 21px;
  font-weight: 400;
  white-space: nowrap;
}

.applied-filter span:last-child {
  color: #000;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  white-space: nowrap;
}

.applied-filter svg {
  position: absolute;
  top: 50%;
  right: 16px;
  margin-top: -8px;
}

.clear {
  color: #0e6ef4;
  text-decoration: underline;
  font-size: 16px;
  font-family: Roboto, sans-serif;
  cursor: pointer;
  font-weight: normal;
  margin-top: -10px;
}

@include media-breakpoint-down(sm) {
  .applied-container {
    display: none;
  }
}
</style>
